import { useState, type ReactNode } from "react";
import { Download, ExternalLink, Eye, FileText, Home, MapPin, Phone, ScrollText } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetDocuments } from "@/hooks/useDeals";
import { useDocumentViewer } from "@/components/deals/documents/DocumentViewer";
import { formatUtcDate } from "@/utils/common.utils";
import { formatDealDateTime, formatInspectionWindow, formatMoney, LOAN_LABELS } from "@/utils/deals.utils";
import { daysOnMarket } from "@/utils/deals-command.utils";
import { DealPhase, DealType, DocumentCategory, Financing, PurchaseMethod } from "@/enum/common.enum";
import type { ClientCalmDeal, ContractTerms, DealDocument } from "@/types/deals.types";
import { buildDealSummaryPrintPath } from "@/routes/url";

/**
 * The contract, in full, for the person who signed it.
 *
 * The client portal is deliberately narrative — the Concierge Letter shows what
 * is happening now and what happens next, and iteration 58 stripped the
 * reference material out of it on purpose. But "quiet" is not the same as
 * "withheld": a client is a *party* to this contract and periodically wants the
 * whole of it — what they agreed to pay, when the deadlines fall, who is
 * holding the earnest money, where they turn up on closing day. Without this
 * they had to email their agent to be told a term they had already signed.
 *
 * Read-only, and no wider than what the portal already receives.
 * `contract_terms` is on the `client_calm` payload today and every field here
 * comes out of it, so nothing new is exposed — this only stops the client from
 * having to ask for what was already on their screen's own data.
 *
 * A dialog rather than a route: it is reference material consulted mid-scroll,
 * and sending someone to another page to read one deadline loses their place in
 * the journey they were reading.
 */
interface ClientContractSheetProps {
  deal: ClientCalmDeal;
  open: boolean;
  onClose: () => void;
}

export default function ClientContractSheet({ deal, open, onClose }: ClientContractSheetProps) {
  const ct: ContractTerms = deal.contract_terms || {};
  // Only fetched while the sheet is open. A client who never opens it should
  // not pay for a request whose result is never rendered.
  const { data: docsData } = useGetDocuments(deal.id, { enabled: open });
  const { openDocument, viewer: documentViewer } = useDocumentViewer();
  const docs = docsData ?? [];

  const isCash = ct.purchase_method === PurchaseMethod.CASH
    || (!ct.purchase_method && "financing" in deal && (deal as { financing?: string }).financing === Financing.CASH);

  // Every key is optional and unmigrated, so "no terms yet" is a real state
  // rather than an edge case — a deal that went under contract before the
  // prompt existed has none of them.
  const hasTerms = Object.entries(ct).some(([key, value]) => {
    if (key.startsWith("updated_") || key === "dd_reminder_sent_at") return false;
    return value !== null && value !== undefined && value !== "" && value !== false;
  });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        className="max-h-[86vh] w-[calc(100vw-2rem)] max-w-3xl overflow-y-auto rounded-2xl border-dash-border bg-white p-0 font-dashboard"
        data-testid="client-contract-sheet"
      >
        <DialogHeader className="sticky top-0 z-10 border-b border-dash-border bg-white/95 px-6 py-5 pr-16 backdrop-blur sm:px-8 sm:pr-20">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <DialogTitle className="font-display text-[26px] font-semibold leading-tight tracking-normal text-dash-ink">
                Your contract, in full
              </DialogTitle>
              <DialogDescription className="truncate text-[13px] text-dash-muted">
                {deal.address}
              </DialogDescription>
            </div>
            <ExportPdfButton dealId={deal.id} />
          </div>
        </DialogHeader>

        <div className="px-6 pb-8 sm:px-8">
          {!hasTerms ? (
            <p className="py-10 text-center text-sm text-dash-muted" data-testid="client-contract-empty">
              Your agent hasn’t published the contract terms yet. They’ll appear
              here as soon as they do.
            </p>
          ) : (
            <>
              {/* The two numbers a client checks first, and the only two that
                  earn display type here — everything below is reference. */}
              <div className="grid gap-6 border-b border-dash-border py-7 sm:grid-cols-2">
                <Headline
                  label="Purchase price"
                  value={formatMoney(ct.purchase_price) || "—"}
                  testid="contract-purchase-price"
                />
                <Headline
                  label="Closing date"
                  value={formatUtcDate(ct.closing_date, { year: "numeric" }, "—")}
                  testid="contract-closing-date"
                />
              </div>

              <Section title="The money">
                <Row k="Earnest money" v={formatMoney(ct.earnest_money_deposit)} testid="contract-emd" />
                <Row k="Earnest money due" v={ct.earnest_money_due_date ? formatUtcDate(ct.earnest_money_due_date, { year: "numeric" }) : null} testid="contract-emd-due" />
                <Row k="Held by" v={ct.earnest_money_holder} testid="contract-emd-holder" />
                <Row k="Option / due-diligence fee" v={formatMoney(ct.option_fee_amount)} testid="contract-option-fee" />
                <Row k="Due-diligence fee due" v={ct.option_fee_due_date ? formatUtcDate(ct.option_fee_due_date, { year: "numeric" }) : null} testid="contract-option-fee-due" />
                <Row
                  k="How you’re paying"
                  v={ct.purchase_method ? (isCash ? "Cash" : "Financed") : null}
                  testid="contract-purchase-method"
                />
                {/* A cash purchase has no loan, so the row is absent rather than
                    an em-dash that reads as "not filled in yet". */}
                {!isCash && (
                  <Row
                    k="Loan type"
                    v={ct.loan_type ? LOAN_LABELS[ct.loan_type] : null}
                    testid="contract-loan-type"
                  />
                )}
              </Section>

              <Section title="Dates & deadlines">
                <Row
                  k="Due diligence ends"
                  v={formatUtcDate(ct.due_diligence_end_date, { year: "numeric" })}
                  testid="contract-dd-end"
                />
                <Row
                  k="Inspection"
                  v={ct.inspection_date
                    ? [formatUtcDate(ct.inspection_date, { year: "numeric" }), formatInspectionWindow(ct)].filter(Boolean).join(", ")
                    : null}
                  testid="contract-inspection-date"
                />
                <Row k="Inspection company" v={ct.inspection_company} testid="contract-inspection-company" />
                {/* Older deals captured inspection *deadlines*; the terms form no
                    longer asks for them, so they show only where one was saved. */}
                {ct.inspection_deadline_1 && (
                  <Row k="Inspection deadline" v={formatUtcDate(ct.inspection_deadline_1, { year: "numeric" })} testid="contract-inspection-1" />
                )}
                {ct.inspection_deadline_2 && (
                  <Row k="Second inspection deadline" v={formatUtcDate(ct.inspection_deadline_2, { year: "numeric" })} testid="contract-inspection-2" />
                )}
                {ct.possession_datetime && (
                  <Row k="Possession" v={formatDealDateTime(ct.possession_datetime)} testid="contract-possession" />
                )}
                {ct.utility_connect_date && (
                  <Row k="Utilities connect" v={formatUtcDate(ct.utility_connect_date, { year: "numeric" })} testid="contract-utility-connect" />
                )}
                {ct.utility_disconnect_date && (
                  <Row k="Utilities disconnect" v={formatUtcDate(ct.utility_disconnect_date, { year: "numeric" })} testid="contract-utility-disconnect" />
                )}
                {!isCash && (
                  <Row
                    k="Financing contingency"
                    // Both inputs, same as the countdown cards: a contingency
                    // entered as "60 days" is not a missing contingency.
                    v={
                      ct.financing_contingency_date
                        ? `By ${formatUtcDate(ct.financing_contingency_date, { year: "numeric" })}`
                        : ct.financing_contingency_days != null
                        ? `${ct.financing_contingency_days} days from the contract date`
                        : null
                    }
                    testid="contract-fin-contingency"
                  />
                )}
              </Section>

              {(ct.home_sale_contingency_enabled || ct.kickout_clause_enabled) && (
                <Section title="Contingencies">
                  {ct.home_sale_contingency_enabled && (
                    <Row
                      k="Home-sale contingency"
                      v={ct.home_sale_contingency_address
                        ? `Yes — ${ct.home_sale_contingency_address}`
                        : "Yes"}
                      testid="contract-hsc"
                    />
                  )}
                  {ct.kickout_clause_enabled && (
                    <Row
                      k="Kick-out clause"
                      v={ct.kickout_clause_details ? `Yes — ${ct.kickout_clause_details}` : "Yes"}
                      testid="contract-kickout"
                    />
                  )}
                </Section>
              )}

              <Section title="Your side">
                <Row k="Buyer(s)" v={ct.buyer_names} testid="contract-buyers" />
                <Row k="Seller(s)" v={ct.seller_names} testid="contract-sellers" />
                <Row k="Closing law firm" v={ct.closing_law_firm} testid="contract-law-firm" />
              </Section>

              <Section title="The other side">
                <Row k="Listing agent" v={ct.listing_agent} testid="contract-listing-agent" />
                <Row k="Listing brokerage" v={ct.listing_brokerage} testid="contract-listing-brokerage" />
                <Row k="Buyer’s agent" v={ct.coop_agent} testid="contract-coop-agent" />
                <Row k="Buyer’s-agent brokerage" v={ct.coop_brokerage} testid="contract-coop-brokerage" />
              </Section>

              {(ct.closing_address || ct.closing_datetime || ct.closing_contact_name || ct.closing_contact_phone) && (
                <Section title="Closing day" icon={MapPin}>
                  {ct.closing_address && (
                    <p className="whitespace-pre-wrap break-words pb-3 font-display text-xl font-semibold leading-snug text-dash-ink" data-testid="contract-closing-address">
                      {ct.closing_address}
                    </p>
                  )}
                  <Row k="When" v={ct.closing_datetime ? formatDealDateTime(ct.closing_datetime) : null} testid="contract-closing-when" />
                  <Row k="Who to ask for" v={ct.closing_contact_name} testid="contract-closing-contact" />
                  {ct.closing_contact_phone && (
                    <Row
                      k="Phone"
                      v={
                        <a href={`tel:${ct.closing_contact_phone}`} className="inline-flex items-center gap-1.5 text-dash-greenDeep hover:underline">
                          <Phone className="size-3.5" />
                          {ct.closing_contact_phone}
                        </a>
                      }
                      testid="contract-closing-phone"
                    />
                  )}
                </Section>
              )}

              {ct.inspection_notes && (
                <Note title="About the inspection" body={ct.inspection_notes} testid="contract-inspection-notes" />
              )}
              {ct.special_stipulations && (
                <Note title="Additional notes" body={ct.special_stipulations} testid="contract-stipulations" />
              )}
              {ct.further_considerations && (
                <Note title="Further considerations" body={ct.further_considerations} testid="contract-further" />
              )}
            </>
          )}

          {/* Documents the agent chose to share. Absent rather than an empty
              heading when there are none — the server already filters this list
              to what a client may see, so "none" means none were shared. */}
          {docs.length > 0 && (
            <Section title="Paperwork your agent shared" icon={FileText}>
              <ul className="divide-y divide-dash-border" data-testid="contract-documents">
                {docs.map((doc) => (
                  <li key={doc.id} className="flex items-center justify-between gap-4 py-3">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-dash-ink">{doc.name}</span>
                      <span className="block text-[11px] uppercase tracking-[0.14em] text-dash-faint">
                        {String(doc.category || "").replace(/_/g, " ")}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex shrink-0 items-center gap-1.5 rounded-full border border-dash-border px-3 py-1.5 text-[12px] font-medium text-dash-muted transition-colors hover:bg-dash-hover hover:text-dash-ink"
                      data-testid={`contract-document-${doc.id}`}
                    >
                      <Eye className="size-3.5" />
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </DialogContent>
      {documentViewer}
    </Dialog>
  );
}

interface ContractSheetTriggerProps {
  deal: ClientCalmDeal;
  side: string;
  docs: DealDocument[];
  onOpenDocument: (doc: DealDocument) => void;
}

/** Compact contract reference card for the client's right rail. Before there
 *  is a contract it is a Listing card for a seller and absent for a buyer —
 *  `contract_terms.purchase_price` is pre-filled from the list price at intake,
 *  so "has terms" alone showed a "Contract" on a home that was only listed. */
export function ContractSheetTrigger({ deal, side, docs, onOpenDocument }: ContractSheetTriggerProps) {
  const [open, setOpen] = useState(false);
  const ct = deal.contract_terms || {};
  const preContract = deal.phase !== DealPhase.UNDER_CONTRACT && deal.phase !== DealPhase.CLOSED;
  if (preContract) {
    return side === DealType.SELL ? <ListingCard deal={deal} docs={docs} onOpenDocument={onOpenDocument} /> : null;
  }
  if (!deal.contract_terms) return null;

  const exportPdf = () => {
    window.open(buildDealSummaryPrintPath(deal.id), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <section
        className="animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-dash-border bg-white p-5 duration-500 delay-200 ease-out"
        data-testid="client-contract-trigger"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-dash-hover text-dash-greenDeep">
              <ScrollText className="size-4" />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-lg font-semibold leading-tight text-dash-ink">Contract</h2>
              <p className="mt-0.5 text-[12px] text-dash-muted">Your key terms</p>
            </div>
          </div>
          <span className="rounded-full bg-dash-hover px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-dash-greenDeep">
            Ready
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 border-y border-dash-border py-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-dash-muted">Purchase price</div>
            <div className="mt-1 truncate font-display text-xl font-semibold leading-tight text-dash-ink">
              {formatMoney(ct.purchase_price) || "—"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-dash-muted">Closing date</div>
            <div className="mt-1 truncate font-display text-xl font-semibold leading-tight text-dash-ink">
              {formatUtcDate(ct.closing_date, { year: "numeric" }, "—")}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex min-h-9 flex-1 items-center justify-center rounded-xl bg-dash-greenDeep px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-dash-sidebarDark"
            data-testid="client-contract-view"
          >
            View full contract
          </button>
          <button
            type="button"
            onClick={exportPdf}
            className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-xl border border-dash-border px-3 py-2 text-[12px] font-semibold text-dash-muted transition-colors hover:border-dash-greenMid hover:text-dash-greenDeep"
            data-testid="client-contract-export-pdf"
          >
            <Download className="size-3.5" />
            Export PDF
          </button>
        </div>
      </section>
      <ClientContractSheet deal={deal} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ListingCard({ deal, docs, onOpenDocument }: Omit<ContractSheetTriggerProps, "side">) {
  const live = deal.phase === DealPhase.LISTING;
  const price = deal.list_price ?? deal.contract_terms?.purchase_price;
  const dom = live ? daysOnMarket(deal, new Date()) : null;
  // During listing the "Contract" document is the listing agreement — see
  // REQUIRED_DOCUMENTS_FOR in backend/config.py. Only offered once the agent
  // has shared it; the server already trims `docs` to what a client may see.
  const agreement = docs.find((d) => d.category === DocumentCategory.CONTRACT);

  return (
    <section
      className="animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-dash-border bg-white p-5 duration-500 delay-200 ease-out"
      data-testid="client-listing-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-dash-hover text-dash-greenDeep">
            <Home className="size-4" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold leading-tight text-dash-ink">Listing</h2>
            <p className="mt-0.5 text-[12px] text-dash-muted">{live ? "Your home is on the market" : "Getting ready to list"}</p>
          </div>
        </div>
        <span className="rounded-full bg-dash-hover px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-dash-greenDeep">
          {live ? "Active" : "Coming soon"}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 border-y border-dash-border py-4">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-dash-muted">Listing price</div>
          <div className="mt-1 truncate font-display text-xl font-semibold leading-tight text-dash-ink" data-testid="client-listing-price">
            {formatMoney(price) || "—"}
          </div>
        </div>
        {dom !== null && (
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-dash-muted">Days on market</div>
            <div className="mt-1 truncate font-display text-xl font-semibold leading-tight text-dash-ink" data-testid="client-listing-dom">
              {dom}
            </div>
          </div>
        )}
      </div>

      {(deal.listing_url || agreement) && (
        <div className="mt-4 flex flex-col gap-2">
          {deal.listing_url && (
            <a
              href={deal.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-xl bg-dash-greenDeep px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-dash-sidebarDark"
              data-testid="client-listing-link"
            >
              View your listing <ExternalLink className="size-3.5" />
            </a>
          )}
          {agreement && (
            <button
              type="button"
              onClick={() => onOpenDocument(agreement)}
              className="inline-flex min-h-9 items-center justify-center rounded-xl border border-dash-border px-3 py-2 text-[12px] font-semibold text-dash-muted transition-colors hover:border-dash-greenMid hover:text-dash-greenDeep"
              data-testid="client-listing-agreement"
            >
              View full listing agreement
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function ExportPdfButton({ dealId }: { dealId: string }) {
  return (
    <button
      type="button"
      onClick={() => window.open(buildDealSummaryPrintPath(dealId), "_blank", "noopener,noreferrer")}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-dash-border px-3 py-2 text-[12px] font-semibold text-dash-muted transition-colors hover:border-dash-greenMid hover:text-dash-greenDeep"
      data-testid="client-contract-sheet-export-pdf"
    >
      <Download className="size-3.5" />
      Export PDF
    </button>
  );
}

function Headline({ label, value, testid }: { label: string; value: string; testid: string }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-dash-muted">{label}</div>
      <div className="mt-2 font-display text-[32px] font-semibold leading-none tracking-normal text-dash-ink">
        {value}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }: { title: string; icon?: typeof MapPin; children: ReactNode }) {
  return (
    <section className="border-b border-dash-border py-6 last:border-b-0">
      <h3 className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-dash-muted">
        {Icon && <Icon className="size-3" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A label/value line. Renders an em-dash for a term the agent has not captured
 *  yet, rather than vanishing — a client reading a contract needs to see that a
 *  field exists and is blank, not silently miss it. */
function Row({ k, v, testid }: { k: string; v?: ReactNode; testid: string }) {
  const empty = v === null || v === undefined || v === "";
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-dash-border/60 py-2.5 last:border-b-0" data-testid={testid}>
      <dt className="shrink-0 text-[13px] text-dash-muted">{k}</dt>
      <dd className={`min-w-0 break-words text-right text-sm ${empty ? "text-dash-faint" : "text-dash-ink"}`}>
        {empty ? "—" : v}
      </dd>
    </div>
  );
}

function Note({ title, body, testid }: { title: string; body: string; testid: string }) {
  return (
    <section className="border-b border-dash-border py-6 last:border-b-0">
      <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.24em] text-dash-muted">{title}</h3>
      <p className="whitespace-pre-wrap text-sm leading-6 text-dash-ink" data-testid={testid}>{body}</p>
    </section>
  );
}
